import React, { useState, useRef } from 'react';
import api from '../../utils/api';
import { API_CONFIG } from '../../utils/apiConfig';

const WhatsappProfile = () => {
    const [status, setStatus] = useState('');
    const [description, setDescription] = useState('');
    const [websites, setWebsites] = useState(['', '']);
    const [profilePicture, setProfilePicture] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const fileRef = useRef(null);

    const handleWebsiteChange = (index, value) => {
        const updated = [...websites];
        updated[index] = value;
        setWebsites(updated);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            setError('File size must be less than 5MB');
            return;
        }

        if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
            setError('Only JPEG, JPG, and PNG files are allowed');
            return;
        }

        setProfilePicture(file);
        setPreview(URL.createObjectURL(file));
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            const formData = new FormData();

            if (status.trim()) {
                formData.append('status', status.trim());
            }

            if (description.trim()) {
                formData.append('description', description.trim());
            }

            const validWebsites = websites.filter(w => w.trim() && w.trim().startsWith('http'));
            if (validWebsites.length > 0) {
                formData.append('websites', validWebsites.join(','));
            }

            if (profilePicture) {
                formData.append('profile_picture', profilePicture);
            }

            if (!status.trim() && !description.trim() && validWebsites.length === 0 && !profilePicture) {
                setError('Please provide at least one field to update');
                setLoading(false);
                return;
            }

            const response = await api.post(API_CONFIG.ADMIN.WHATSAPP_PROFILE, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                setSuccess('WhatsApp business profile updated successfully');
                setProfilePicture(null);
                setPreview(null);
                if (fileRef.current) fileRef.current.value = '';
            } else {
                setError(response.data.message || 'Failed to update profile');
            }
        } catch (error) {
            setError(error.response?.data?.message || error.response?.data?.error || 'Failed to update WhatsApp profile');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">WhatsApp Business Profile</h1>
                <p className="text-gray-600">Update your WhatsApp business profile information</p>
            </div>

            {error && (
                <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {success && (
                <div className="mb-4 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded">
                    {success}
                </div>
            )}

            <div className="bg-white shadow sm:rounded-lg max-w-2xl">
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            About / Status <span className="text-gray-400">(max 139 characters)</span>
                        </label>
                        <textarea
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            maxLength={139}
                            rows={2}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter your short business status or motto..."
                        />
                        <p className="text-xs text-gray-500 mt-1">{status.length}/139 characters</p>
                        <p className="text-xs text-gray-400 mt-0.5">This appears as your short status in the profile</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Business Description <span className="text-gray-400">(max 512 characters)</span>
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            maxLength={512}
                            rows={5}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Describe your business in detail. What products or services do you offer? What makes your business unique? This helps customers understand what you do."
                        />
                        <p className="text-xs text-gray-500 mt-1">{description.length}/512 characters</p>
                        <p className="text-xs text-gray-400 mt-0.5">Detailed description of your business (appears below your status)</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">
                            Websites <span className="text-gray-400">(max 2, must include http:// or https://)</span>
                        </label>
                        {websites.map((url, index) => (
                            <input
                                key={index}
                                type="url"
                                value={url}
                                onChange={(e) => handleWebsiteChange(index, e.target.value)}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`Website URL ${index + 1} (e.g., https://paymeo.co)`}
                            />
                        ))}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Profile Picture</label>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".jpg,.jpeg,.png"
                            onChange={handleFileChange}
                            className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <p className="text-xs text-gray-500 mt-1">JPEG, JPG, or PNG. Max 5MB. Square image recommended (640x640 pixels).</p>
                        {preview && (
                            <div className="mt-3">
                                <img src={preview} alt="Preview" className="h-20 w-20 object-cover rounded-full border" />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setProfilePicture(null);
                                        setPreview(null);
                                        if (fileRef.current) fileRef.current.value = '';
                                    }}
                                    className="mt-1 text-xs text-red-600 hover:text-red-800"
                                >
                                    Remove
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        {loading ? 'Updating...' : 'Update Profile'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default WhatsappProfile;